import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Chip,
  Divider,
  Tooltip,
  Grid,
} from "@mui/material";
import Countdown from "react-countdown";
import "../Tools.css"; // Import wood-themed styling

// Icons
import TimerIcon from "@mui/icons-material/Timer";
import StarsIcon from "@mui/icons-material/Stars";
import PercentIcon from "@mui/icons-material/Percent";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DiamondIcon from "@mui/icons-material/Diamond";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import LockIcon from "@mui/icons-material/Lock";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

interface Pool {
  id: number;
  min_stake: string;
  total_staked: string;
  stake_delay: number;
  base_apr: number;
  monthly_increase: number;
  is_open: boolean;
  reward_type: string;
}

interface Staker {
  id: number;
  pool_id: number;
  username: string;
  staked_amount: string;
  pending_rewards: string;
  end_time: number;
  start_time: number;
  last_reward_update: number;
}

interface StakingPoolCardProps {
  pool: Pool;
  stakerInfo: Staker | null;
  onStake: () => void;
  onClaim: () => void;
  onUnstake: () => void;
}

export const StakingPoolCard = ({
  pool,
  stakerInfo,
  onStake,
  onClaim,
  onUnstake,
}: StakingPoolCardProps) => {
  const isStaked = stakerInfo?.pool_id === pool.id;
  const timeUntilUnlock = isStaked ? 0 : null;
  
  // Calculate the reward per day based on APR
  const dailyAPR = pool.base_apr / 365;
  const dailyRewardPercentage = (dailyAPR * 100).toFixed(4);

  // Determine pool tier for styling
  const getPoolTier = (poolId: number) => {
    switch (poolId) {
      case 1:
        return {
          name: "Bronze",
          color: "#cd7f32",
          bgGradient: "linear-gradient(135deg, #614023 0%, #966946 100%)",
        };
      case 2:
        return {
          name: "Silver",
          color: "#C0C0C0",
          bgGradient: "linear-gradient(135deg, #7D7D7D 0%, #C0C0C0 100%)",
        };
      case 3:
        return {
          name: "Gold",
          color: "#FFD700",
          bgGradient: "linear-gradient(135deg, #B8860B 0%, #FFD700 100%)",
        };
      case 4:
        return {
          name: "Platinum",
          color: "#E5E4E2",
          bgGradient: "linear-gradient(135deg, #8B8B8B 0%, #E5E4E2 100%)",
        };
      default:
        return {
          name: "Standard",
          color: "#f5e1a3",
          bgGradient: "linear-gradient(135deg, #5c3c10 0%, #7d5014 100%)",
        };
    }
  };

  const poolTier = getPoolTier(pool.id);

  return (
    <Card
      className="stats-card"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        borderRadius: "12px",
        position: "relative",
        border: `4px solid ${poolTier.color}`,
        background: poolTier.bgGradient,
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(45deg, rgba(92, 60, 16, 0.2) 25%, transparent 25%, transparent 50%, rgba(92, 60, 16, 0.2) 50%, rgba(92, 60, 16, 0.2) 75%, transparent 75%, transparent)",
          backgroundSize: "10px 10px",
          opacity: 0.3,
          borderRadius: "8px",
          zIndex: 0,
        }
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Pool Header */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: poolTier.color, 
                fontWeight: "bold",
                textShadow: "1px 1px 2px rgba(0,0,0,0.7)"
              }}
            >
              {poolTier.name} Pool
            </Typography>
            <Chip 
              label={`#${pool.id}`}
              size="small"
              sx={{ 
                bgcolor: "rgba(245, 225, 163, 0.2)", 
                color: poolTier.color,
                border: `1px solid ${poolTier.color}`,
                fontWeight: "bold"
              }}
            />
          </Box>
          
          <Divider sx={{ borderColor: "rgba(245, 225, 163, 0.3)", mb: 2 }} />
        </Box>

        {/* Pool Info */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Tooltip title="Annual Percentage Rate">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PercentIcon sx={{ color: "gold", mr: 1, fontSize: "1.2rem" }} />
                <Box>
                  <Typography variant="body2" sx={{ color: "#f5e1a3" }}>
                    APR
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "bold", color: "white" }}>
                    {(pool.base_apr * 100).toFixed(2)}%
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          </Grid>
          
          <Grid item xs={6}>
            <Tooltip title="Monthly APR Increase">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ArrowUpwardIcon sx={{ color: "#4CAF50", mr: 1, fontSize: "1.2rem" }} />
                <Box>
                  <Typography variant="body2" sx={{ color: "#f5e1a3" }}>
                    Monthly
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "bold", color: "white" }}>
                    +{(pool.monthly_increase * 100).toFixed(2)}%
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          </Grid>
          
          <Grid item xs={6}>
            <Tooltip title="Lock Period Duration">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LockIcon sx={{ color: "lightblue", mr: 1, fontSize: "1.2rem" }} />
                <Box>
                  <Typography variant="body2" sx={{ color: "#f5e1a3" }}>
                    Lock Period
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "bold", color: "white" }}>
                    {pool.stake_delay / 86400} days
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          </Grid>
          
          <Grid item xs={6}>
            <Tooltip title="Minimum Stake Amount">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AccountBalanceWalletIcon sx={{ color: "#f5e1a3", mr: 1, fontSize: "1.2rem" }} />
                <Box>
                  <Typography variant="body2" sx={{ color: "#f5e1a3" }}>
                    Min Stake
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "bold", color: "white" }}>
                    {pool.min_stake}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Daily Rewards Info */}
        <Box 
          sx={{ 
            mb: 2, 
            p: 1.5, 
            borderRadius: "8px", 
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            border: "1px solid rgba(245, 225, 163, 0.2)"
          }}
        >
          <Typography variant="body2" sx={{ color: "#f5e1a3", mb: 0.5, fontWeight: "bold" }}>
            <DiamondIcon sx={{ fontSize: "0.9rem", mr: 0.5, verticalAlign: "middle" }} />
            Daily Rewards
          </Typography>
          <Typography variant="body2" sx={{ color: "white" }}>
            Each day you'll earn {dailyRewardPercentage}% of your staked amount
          </Typography>
        </Box>

        {/* User Staking Info */}
        {isStaked && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 1.5, 
              borderRadius: "8px", 
              backgroundColor: "rgba(245, 225, 163, 0.1)",
              border: "1px solid rgba(245, 225, 163, 0.3)"
            }}
          >
            <Typography variant="body2" sx={{ color: "#f5e1a3", mb: 1, fontWeight: "bold" }}>
              Your Stake
            </Typography>
            
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: "#f5e1a3" }}>
                  Amount:
                </Typography>
                <Typography variant="body2" sx={{ color: "white", fontWeight: "bold" }}>
                  {stakerInfo?.staked_amount}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: "#f5e1a3" }}>
                  Pending:
                </Typography>
                <Typography variant="body2" sx={{ color: "white", fontWeight: "bold" }}>
                  {stakerInfo?.pending_rewards}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1, flexDirection: "column", mt: "auto" }}>
          {!isStaked ? (
            <Button
              variant="contained"
              onClick={onStake}
              disabled={!pool.is_open}
              endIcon={<StarsIcon />}
              className="action-button"
              sx={{
                backgroundColor: "#5c3c10",
                color: "#f5e1a3",
                fontWeight: "bold",
                borderRadius: "8px",
                padding: "10px 16px",
                "&:hover": { 
                  backgroundColor: "#4a300b",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(92, 60, 16, 0.4)",
                  color: "rgba(245, 225, 163, 0.4)",
                }
              }}
            >
              Stake ANTLERS
            </Button>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ position: "relative" }}>
                <Tooltip title={
                  Date.now() / 1000 < stakerInfo.last_reward_update + 86400
                    ? "You can claim rewards once per day"
                    : "Claim your pending rewards"
                }>
                  <span>
                    <Button
                      variant="contained"
                      onClick={onClaim}
                      disabled={Date.now() / 1000 < stakerInfo.last_reward_update + 86400}
                      fullWidth
                      className="action-button"
                      startIcon={<DiamondIcon />}
                      sx={{
                        backgroundColor: "#5c3c10",
                        color: "#f5e1a3",
                        fontWeight: "bold",
                        borderRadius: "8px",
                        padding: "10px 16px",
                        "&:hover": { 
                          backgroundColor: "#4a300b",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "rgba(92, 60, 16, 0.4)",
                          color: "rgba(245, 225, 163, 0.4)",
                        }
                      }}
                    >
                      {Date.now() / 1000 < stakerInfo.last_reward_update + 86400 ? (
                        <Countdown 
                          date={(stakerInfo.last_reward_update + 86400) * 1000}
                          renderer={({ hours, minutes, seconds }) => (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <HourglassEmptyIcon sx={{ mr: 1, fontSize: "1rem" }} />
                              <span>{hours}h {minutes}m {seconds}s</span>
                            </Box>
                          )}
                        />
                      ) : (
                        "Claim Rewards"
                      )}
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              <Box sx={{ position: "relative" }}>
                <Tooltip title={
                  Date.now() / 1000 < stakerInfo.end_time
                    ? `Locked until ${new Date(stakerInfo.end_time * 1000).toLocaleDateString()}`
                    : "Unstake your tokens and rewards"
                }>
                  <span>
                    <Button
                      variant="outlined"
                      onClick={onUnstake}
                      disabled={Date.now() / 1000 < stakerInfo.end_time}
                      fullWidth
                      className="action-button"
                      startIcon={<LockIcon />}
                      sx={{
                        borderColor: "#f5e1a3",
                        color: "#f5e1a3",
                        fontWeight: "bold",
                        borderRadius: "8px",
                        padding: "10px 16px",
                        "&:hover": { 
                          borderColor: "white",
                          backgroundColor: "rgba(245, 225, 163, 0.1)",
                        },
                        "&.Mui-disabled": {
                          borderColor: "rgba(245, 225, 163, 0.3)",
                          color: "rgba(245, 225, 163, 0.3)",
                        }
                      }}
                    >
                      {Date.now() / 1000 < stakerInfo.end_time ? (
                        <Countdown 
                          date={stakerInfo.end_time * 1000}
                          renderer={({ days, hours, minutes }) => (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <LockIcon sx={{ mr: 1, fontSize: "1rem" }} />
                              <span>{days}d {hours}h {minutes}m</span>
                            </Box>
                          )}
                        />
                      ) : (
                        "Unstake"
                      )}
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              <Button
                variant="contained"
                onClick={onStake}
                fullWidth
                className="action-button"
                sx={{
                  backgroundColor: "#2C3E0A",
                  color: "#f5e1a3",
                  fontWeight: "bold",
                  borderRadius: "8px",
                  padding: "10px 16px",
                  "&:hover": { 
                    backgroundColor: "#3A5210",
                  },
                }}
              >
                Add More ANTLERS
              </Button>
              <Typography variant="caption" sx={{ color: "#f5e1a3", textAlign: "center", fontStyle: "italic" }}>
                *Adding more tokens will reset your lock period*
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
